import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './hooks/useAuth'
import { NotificationsProvider } from './hooks/useNotifications'
import { ProfileProvider } from './context/ProfileContext'
import { ToastProvider } from './components/ui/NotificationToast'
import ProtectedRoute from './components/ProtectedRoute'
import RequireFinancialAccess from './components/RequireFinancialAccess'
import RootRedirect from './components/RootRedirect'
import LandlordLayout from './components/LandlordLayout'
import TenantLayout from './components/TenantLayout'
import ContractorLayout from './components/ContractorLayout'
import LandlordClientLayout from './components/LandlordClientLayout'
import ProspectLayout from './components/ProspectLayout'
import PageLoadingSpinner from './components/PageLoadingSpinner'
import PasswordGate from './components/auth/PasswordGate'
import CookieConsent from './components/CookieConsent'

// ---------------------------------------------------------------------------
// Lazy-loaded page components (route-based code splitting)
// ---------------------------------------------------------------------------

// Legal pages
const PrivacyPage = lazy(() => import('./pages/legal/Privacy'))
const TermsPage = lazy(() => import('./pages/legal/Terms'))
const GdprPage = lazy(() => import('./pages/legal/Gdpr'))

// Auth / public pages
const Login = lazy(() => import('./pages/Login'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const OnboardingSuccess = lazy(() => import('./pages/OnboardingSuccess'))
const ContractorInvite = lazy(() => import('./pages/ContractorInvite'))
const ProspectInvite = lazy(() => import('./pages/ProspectInvite'))
const TenantInvite = lazy(() => import('./pages/TenantInvite'))
const LandlordClientInvite = lazy(() => import('./pages/LandlordClientInvite'))
const SharedJob = lazy(() => import('./pages/SharedJob'))

// Landlord / Agent pages
const Dashboard = lazy(() => import('./pages/landlord/Dashboard'))
const Issues = lazy(() => import('./pages/landlord/Issues'))
const MaintenanceDetail = lazy(() => import('./pages/landlord/MaintenanceDetail'))
const Properties = lazy(() => import('./pages/landlord/Properties'))
const PropertyDetail = lazy(() => import('./pages/landlord/PropertyDetail'))
const PropertyIssueDetail = lazy(() => import('./pages/landlord/PropertyIssueDetail'))
const PropertyForm = lazy(() => import('./pages/landlord/PropertyForm'))
const QRCodes = lazy(() => import('./pages/landlord/QRCodes'))
const Contractors = lazy(() => import('./pages/landlord/Contractors'))
const ContractorDetail = lazy(() => import('./pages/landlord/ContractorDetail'))
const Reports = lazy(() => import('./pages/landlord/Reports'))
const Compliance = lazy(() => import('./pages/landlord/Compliance'))
const ComplianceDetail = lazy(() => import('./pages/landlord/ComplianceDetail'))
const Tenants = lazy(() => import('./pages/landlord/Tenants'))
const TenancyDetail = lazy(() => import('./pages/landlord/TenancyDetail'))
const Financials = lazy(() => import('./pages/landlord/Financials'))
const Expenses = lazy(() => import('./pages/landlord/Expenses'))
const IncomeList = lazy(() => import('./pages/landlord/Income'))
const RentPayments = lazy(() => import('./pages/landlord/RentPayments'))
const Transactions = lazy(() => import('./pages/landlord/Transactions'))
const FinancialReports = lazy(() => import('./pages/landlord/FinancialReports'))
const NotificationSettings = lazy(() => import('./pages/landlord/NotificationSettings'))
const Settings = lazy(() => import('./pages/landlord/Settings'))
const Billing = lazy(() => import('./pages/landlord/Billing'))
const BillingSuccess = lazy(() => import('./pages/landlord/BillingSuccess'))
const XeroCallbackPage = lazy(() => import('./pages/landlord/XeroCallbackPage'))
const Prospects = lazy(() => import('./pages/landlord/Prospects'))
const ProspectDetail = lazy(() => import('./pages/landlord/ProspectDetail'))
const Viewings = lazy(() => import('./pages/landlord/Viewings'))
const ViewingDetail = lazy(() => import('./pages/landlord/ViewingDetail'))
const CalendarPage = lazy(() => import('./pages/landlord/Calendar'))
const Activity = lazy(() => import('./pages/landlord/Activity'))
const Portfolios = lazy(() => import('./pages/landlord/Portfolios'))
const PortfolioDetail = lazy(() => import('./pages/landlord/PortfolioDetail'))
const Team = lazy(() => import('./pages/landlord/Team'))
const TeamInvite = lazy(() => import('./pages/landlord/TeamInvite'))
const Import = lazy(() => import('./pages/landlord/Import'))
const InformationSheetBulkServe = lazy(() => import('./pages/landlord/InformationSheetBulkServe'))

// Landlord-client portal pages
const ClientDashboard = lazy(() => import('./pages/landlord-client/Dashboard'))
const ClientProperties = lazy(() => import('./pages/landlord-client/Properties'))
const ClientIssues = lazy(() => import('./pages/landlord-client/Issues'))
const ClientCompliance = lazy(() => import('./pages/landlord-client/Compliance'))
const ClientFinancials = lazy(() => import('./pages/landlord-client/Financials'))
const ClientSpendApprovals = lazy(() => import('./pages/landlord-client/SpendApprovals'))

// Tenant pages
const ReportIssue = lazy(() => import('./pages/tenant/ReportIssue'))
const MyIssues = lazy(() => import('./pages/tenant/MyIssues'))
const TenantIssueDetail = lazy(() => import('./pages/tenant/IssueDetail'))
const Portal = lazy(() => import('./pages/tenant/Portal'))
const MyTenancy = lazy(() => import('./pages/tenant/MyTenancy'))
const MyDeposit = lazy(() => import('./pages/tenant/MyDeposit'))
const MyDocuments = lazy(() => import('./pages/tenant/MyDocuments'))
const MyProperty = lazy(() => import('./pages/tenant/MyProperty'))
const TenantSettings = lazy(() => import('./pages/tenant/Settings'))
const TenantMessages = lazy(() => import('./pages/tenant/Messages'))

// Contractor pages
const ContractorDashboard = lazy(() => import('./pages/contractor/Dashboard'))
const Jobs = lazy(() => import('./pages/contractor/Jobs'))
const ContractorProfile = lazy(() => import('./pages/contractor/Profile'))
const ContractorSettings = lazy(() => import('./pages/contractor/Settings'))


// Prospect pages
const ProspectPortal = lazy(() => import('./pages/prospect/ProspectPortal'))
const ProspectViewings = lazy(() => import('./pages/prospect/ProspectViewings'))
const ProspectApplication = lazy(() => import('./pages/prospect/ProspectApplication'))
const ProspectDocuments = lazy(() => import('./pages/prospect/ProspectDocuments'))
const ProspectPropertyDetails = lazy(() => import('./pages/prospect/PropertyDetails'))

export default function App() {
  return (
    <BrowserRouter>
      <CookieConsent />
      {/* TODO: Remove PasswordGate when ready for public launch */}
      <PasswordGate>
      <AuthProvider>
      <NotificationsProvider>
      <ProfileProvider>
      <ToastProvider>
        <Routes>
          {/* Root redirect: login (unauthed) or dashboard/portal/jobs (authed) */}
          <Route path="/" element={<RootRedirect />} />

          {/* Legal pages (public, linked from app settings) */}
          <Route path="/legal/privacy" element={<Suspense fallback={<PageLoadingSpinner />}><PrivacyPage /></Suspense>} />
          <Route path="/legal/terms" element={<Suspense fallback={<PageLoadingSpinner />}><TermsPage /></Suspense>} />
          <Route path="/legal/gdpr" element={<Suspense fallback={<PageLoadingSpinner />}><GdprPage /></Suspense>} />

          {/* Public routes */}
          <Route path="/login" element={<Suspense fallback={<PageLoadingSpinner />}><Login /></Suspense>} />
          <Route path="/auth/callback" element={<Suspense fallback={<PageLoadingSpinner />}><AuthCallback /></Suspense>} />
          <Route path="/report/:shortCode" element={<Suspense fallback={<PageLoadingSpinner />}><ReportIssue /></Suspense>} />
          <Route path="/invite/:inviteId" element={<Suspense fallback={<PageLoadingSpinner />}><ContractorInvite /></Suspense>} />
          <Route path="/prospect-invite/:prospectId" element={<Suspense fallback={<PageLoadingSpinner />}><ProspectInvite /></Suspense>} />
          <Route path="/tenant-invite/:inviteId" element={<Suspense fallback={<PageLoadingSpinner />}><TenantInvite /></Suspense>} />
          <Route path="/job/:token" element={<Suspense fallback={<PageLoadingSpinner />}><SharedJob /></Suspense>} />
          <Route path="/client-invite/:token" element={<Suspense fallback={<PageLoadingSpinner />}><LandlordClientInvite /></Suspense>} />
          <Route path="/team/invite" element={<Suspense fallback={<PageLoadingSpinner />}><TeamInvite /></Suspense>} />

          {/* Onboarding - authenticated but no profile */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute requireNoProfile>
                <Suspense fallback={<PageLoadingSpinner />}><Onboarding /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/success"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoadingSpinner />}><OnboardingSuccess /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Landlord / Agent routes — Suspense is in LandlordLayout */}
          <Route
            element={
              <ProtectedRoute roles={['landlord', 'agent']}>
                <LandlordLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/maintenance" element={<Issues />} />
            <Route path="/maintenance/:id" element={<MaintenanceDetail />} />
            <Route path="/scheduled-maintenance" element={<Navigate to="/maintenance?tab=scheduled" replace />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/properties/new" element={<PropertyForm />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/properties/:id/issues/:issueId" element={<PropertyIssueDetail />} />
            <Route path="/properties/:id/edit" element={<PropertyForm />} />
            <Route path="/qr-codes" element={<QRCodes />} />
            <Route path="/contractors" element={<Contractors />} />
            <Route path="/contractors/:id" element={<ContractorDetail />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/compliance/:id" element={<ComplianceDetail />} />
            <Route path="/prospects" element={<Prospects />} />
            <Route path="/prospects/:id" element={<ProspectDetail />} />
            <Route path="/viewings" element={<Viewings />} />
            <Route path="/viewings/:id" element={<ViewingDetail />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/audit-trail" element={<Navigate to="/activity?tab=audit-trail" replace />} />
            <Route path="/tenants" element={<Tenants />} />
            <Route path="/tenants/:id" element={<TenancyDetail />} />
            <Route element={<RequireFinancialAccess />}>
              <Route path="/financials" element={<Financials />} />
              <Route path="/financials/expenses" element={<Expenses />} />
              <Route path="/financials/income" element={<IncomeList />} />
              <Route path="/financials/rent" element={<RentPayments />} />
              <Route path="/financials/transactions" element={<Transactions />} />
              <Route path="/financials/reports" element={<FinancialReports />} />
            </Route>
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/notifications" element={<NotificationSettings />} />
            <Route path="/settings/billing" element={<Billing />} />
            <Route path="/settings/xero/callback" element={<XeroCallbackPage />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/portfolios" element={<Portfolios />} />
            <Route path="/portfolios/:id" element={<PortfolioDetail />} />
            <Route path="/team" element={<Team />} />
            <Route path="/import" element={<Import />} />
            <Route path="/serve-information-sheet" element={<InformationSheetBulkServe />} />
          </Route>

          {/* Tenant routes — Suspense is in TenantLayout */}
          <Route
            element={
              <ProtectedRoute roles={['tenant']}>
                <TenantLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/portal" element={<Portal />} />
            <Route path="/portal/tenancy" element={<MyTenancy />} />
            <Route path="/portal/deposit" element={<MyDeposit />} />
            <Route path="/portal/documents" element={<MyDocuments />} />
            <Route path="/portal/property" element={<MyProperty />} />
            <Route path="/portal/settings" element={<TenantSettings />} />
            <Route path="/my-issues" element={<MyIssues />} />
            <Route path="/my-issues/:id" element={<TenantIssueDetail />} />
            <Route path="/portal/messages" element={<TenantMessages />} />
          </Route>

          {/* Contractor routes — Suspense is in ContractorLayout */}
          <Route
            element={
              <ProtectedRoute roles={['contractor']}>
                <ContractorLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/home" element={<ContractorDashboard />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:id" element={<Jobs />} />
            <Route path="/profile" element={<ContractorProfile />} />
            <Route path="/account" element={<ContractorSettings />} />
          </Route>


          {/* Landlord-client portal routes — Suspense is in LandlordClientLayout */}
          <Route
            element={
              <ProtectedRoute roles={['landlord_client']}>
                <LandlordClientLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/client" element={<ClientDashboard />} />
            <Route path="/client/properties" element={<ClientProperties />} />
            <Route path="/client/issues" element={<ClientIssues />} />
            <Route path="/client/compliance" element={<ClientCompliance />} />
            <Route path="/client/financials" element={<ClientFinancials />} />
            <Route path="/client/approvals" element={<ClientSpendApprovals />} />
          </Route>

          {/* Prospect portal routes — Suspense is in ProspectLayout */}
          <Route
            element={
              <ProtectedRoute roles={['tenant']}>
                <ProspectLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/prospect" element={<ProspectPortal />} />
            <Route path="/prospect/viewings" element={<ProspectViewings />} />
            <Route path="/prospect/application" element={<ProspectApplication />} />
            <Route path="/prospect/documents" element={<ProspectDocuments />} />
            <Route path="/prospect/property/:id" element={<ProspectPropertyDetails />} />
          </Route>

          {/* Catch-all: redirect stale marketing URLs to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
      </ProfileProvider>
      </NotificationsProvider>
      </AuthProvider>
      </PasswordGate>
    </BrowserRouter>
  )
}
